import { randomBytes } from 'node:crypto';
import { createServer, type Socket } from 'node:net';
import { Data, Deferred, Effect, Option, Schema } from 'effect';
import { launchProcessEffect } from './launcher-lifetime';

import { CLIENT_PROTOCOL_HEADER, CLIENT_PROTOCOL_VERSION } from '@okrapdf/schemas/client-protocol';

import type { WorkbenchEndpoint } from './workbench-client.js';

const callbackPath = '/endpoint';
const maximumCallbackBytes = 65_536;

const desktopEndpointErrorCode = Schema.Literals([
  'listen_failed',
  'launch_failed',
  'callback_timeout',
  'callback_invalid',
  'callback_auth_failed',
  'endpoint_invalid',
] as const);

export class DesktopEndpointError extends Schema.TaggedError<DesktopEndpointError>()(
  'DesktopEndpointError',
  {
    code: desktopEndpointErrorCode,
    message: Schema.String,
    retryable: Schema.Boolean,
  },
) {}

const endpointRecordSchema = Schema.Struct({
  protocolVersion: Schema.Literal(CLIENT_PROTOCOL_VERSION),
  baseURL: Schema.String,
  token: Schema.String,
  pid: Schema.Int,
  version: Schema.String,
});

export interface DesktopEndpointDiscoveryOptions {
  readonly timeoutMs?: number;
  readonly launch?: (callbackURL: string, signal?: AbortSignal) => Promise<void>;
}

export class DesktopDiscoveryError extends Data.TaggedError('DesktopDiscoveryError')<{
  readonly code: DesktopEndpointError['code'];
  readonly retryable: boolean;
}> {}

const causes = new WeakMap<DesktopDiscoveryError, DesktopEndpointError>();
export const desktopDiscoveryErrorCause = (error: DesktopDiscoveryError): DesktopEndpointError =>
  causes.get(error)!;

function privateFailure(cause: DesktopEndpointError): DesktopDiscoveryError {
  const error = new DesktopDiscoveryError({ code: cause.code, retryable: cause.retryable });
  causes.set(error, cause);
  return error;
}

function failure(
  code: DesktopEndpointError['code'],
  message: string,
  retryable = false,
): DesktopDiscoveryError {
  return privateFailure(new DesktopEndpointError({ code, message, retryable }));
}

function validLoopbackEndpoint(record: typeof endpointRecordSchema.Type): WorkbenchEndpoint | null {
  try {
    const url = new URL(record.baseURL);
    if (
      url.protocol !== 'http:' ||
      !['127.0.0.1', 'localhost', '::1'].includes(url.hostname) ||
      !url.port ||
      url.username ||
      url.password ||
      !record.token
    ) {
      return null;
    }
    return { baseUrl: url.origin, token: record.token };
  } catch {
    return null;
  }
}

function parseHeaders(headerText: string): {
  readonly requestLine: string;
  readonly headers: Readonly<Record<string, string>>;
} {
  const lines = headerText.split('\r\n');
  const requestLine = lines.shift() ?? '';
  const headers: Record<string, string> = {};
  for (const line of lines) {
    const colon = line.indexOf(':');
    if (colon <= 0) continue;
    headers[line.slice(0, colon).trim().toLowerCase()] = line.slice(colon + 1).trim();
  }
  return { requestLine, headers };
}

const defaultLaunchEffect = Effect.fn('okrapdf/cli.desktop.launch')(function* (
  callbackURL: string,
) {
  const explicitApp = process.env.OKRA_APP_PATH?.trim();
  yield* launchProcessEffect(
    '/usr/bin/open',
    ['-gj', ...(explicitApp ? ['-a', explicitApp] : []), callbackURL, '--args', callbackURL],
    { stdio: 'ignore', windowsHide: true },
  ).pipe(
    Effect.mapError(() =>
      failure('launch_failed', 'LaunchServices could not open Okra.app.', true),
    ),
    Effect.timeoutOrElse({
      duration: 5000,
      orElse: () =>
        Effect.fail(failure('launch_failed', 'LaunchServices did not return promptly.', true)),
    }),
  );
});

/** Callback events publish to native Deferreds; the surrounding scope owns every handle. */
function callbackServerEffect(nonce: string) {
  return Effect.acquireRelease(
    Effect.try({
      try: () => {
        const result = Deferred.makeUnsafe<WorkbenchEndpoint, DesktopDiscoveryError>();
        const ready = Deferred.makeUnsafe<void, DesktopDiscoveryError>();
        const binding = new AbortController();
        const sockets = new Map<Socket, Deferred.Deferred<void>>();
        let stopping = false;
        let claimed = false;
        const fail = (error: DesktopDiscoveryError, failedResponse = false) => {
          if (claimed && !failedResponse) return;
          claimed = true;
          Deferred.doneUnsafe(ready, Effect.fail(error));
          Deferred.doneUnsafe(result, Effect.fail(error));
        };
        const receive = (socket: Socket) => {
          const closed = Deferred.makeUnsafe<void>();
          sockets.set(socket, closed);
          let data = Buffer.alloc(0);
          let responding = false;
          const onError = () => {
            if (!stopping)
              fail(
                failure('callback_invalid', 'Okra.app callback connection failed.', true),
                responding,
              );
          };
          const onClose = () => {
            if (responding && !stopping && !Deferred.isDoneUnsafe(result)) onError();
            socket.off('data', onData);
            socket.off('error', onError);
            socket.off('close', onClose);
            data = Buffer.alloc(0);
            sockets.delete(socket);
            Deferred.doneUnsafe(closed, Effect.void);
          };
          const respond = (
            status: number,
            reason: string,
            outcome: Effect.Effect<WorkbenchEndpoint, DesktopDiscoveryError>,
          ) => {
            if (claimed || stopping) return;
            claimed = true;
            responding = true;
            // Flush this response before publishing success/failure and closing the owner.
            // Partial peers do not control shutdown: the finalizer destroys all sockets.
            socket.end(
              `HTTP/1.1 ${status} ${reason}\r\nContent-Length: 0\r\nConnection: close\r\n\r\n`,
              () => {
                if (!stopping) Deferred.doneUnsafe(result, outcome);
              },
            );
          };
          const reject = (
            status: number,
            reason: string,
            code: DesktopEndpointError['code'],
            message: string,
          ) => respond(status, reason, Effect.fail(failure(code, message)));
          const onData = (chunk: Buffer) => {
            if (claimed || stopping) return;
            data = Buffer.concat([data, chunk]);
            if (data.byteLength > maximumCallbackBytes) {
              reject(
                413,
                'Payload Too Large',
                'callback_invalid',
                'Okra.app callback exceeded 64 KiB.',
              );
              return;
            }
            const headerEnd = data.indexOf('\r\n\r\n');
            if (headerEnd < 0) return;
            const { requestLine, headers } = parseHeaders(
              data.subarray(0, headerEnd).toString('utf8'),
            );
            const contentLength = Number(headers['content-length'] ?? '');
            const bodyStart = headerEnd + 4;
            if (!Number.isInteger(contentLength) || contentLength < 1) {
              reject(
                400,
                'Bad Request',
                'callback_invalid',
                'Okra.app sent an invalid callback body.',
              );
              return;
            }
            if (data.byteLength - bodyStart < contentLength) return;
            if (
              requestLine !== `POST ${callbackPath} HTTP/1.1` ||
              headers.authorization !== `Bearer ${nonce}` ||
              headers[CLIENT_PROTOCOL_HEADER] !== CLIENT_PROTOCOL_VERSION
            ) {
              reject(
                401,
                'Unauthorized',
                'callback_auth_failed',
                'Okra.app callback authentication failed.',
              );
              return;
            }
            let payload: unknown;
            try {
              payload = JSON.parse(
                data.subarray(bodyStart, bodyStart + contentLength).toString('utf8'),
              );
            } catch {
              reject(
                400,
                'Bad Request',
                'callback_invalid',
                'Okra.app sent invalid endpoint JSON.',
              );
              return;
            }
            const decoded = Schema.decodeUnknownOption(endpointRecordSchema)(payload);
            if (Option.isNone(decoded)) {
              reject(
                409,
                'Conflict',
                'endpoint_invalid',
                'Okra.app published an incompatible endpoint.',
              );
              return;
            }
            const endpoint = validLoopbackEndpoint(decoded.value);
            if (!endpoint) {
              reject(
                409,
                'Conflict',
                'endpoint_invalid',
                'Okra.app published a non-loopback endpoint.',
              );
              return;
            }
            respond(204, 'No Content', Effect.succeed(endpoint));
          };
          socket.on('data', onData);
          socket.on('error', onError);
          socket.once('close', onClose);
          if (stopping) socket.destroy();
        };
        const server = createServer();
        const onError = () => {
          if (!stopping)
            fail(failure('listen_failed', 'Could not listen for the Okra.app callback.', true));
        };
        const onListening = () => Deferred.doneUnsafe(ready, Effect.void);
        server.on('connection', receive);
        server.on('error', onError);
        server.once('listening', onListening);
        const listen = Effect.gen(function* () {
          yield* Effect.try({
            try: () => server.listen({ port: 0, host: '127.0.0.1', signal: binding.signal }),
            catch: () =>
              failure('listen_failed', 'Could not listen for the Okra.app callback.', true),
          });
          yield* Deferred.await(ready);
          const address = server.address();
          if (!address || typeof address === 'string') {
            return yield* Effect.fail(
              failure('listen_failed', 'Could not resolve the desktop callback port.', true),
            );
          }
          return address.port;
        });
        const close = Effect.gen(function* () {
          stopping = true;
          const pending = [...sockets.values()];
          yield* Effect.callback<void>((resume) => {
            // Abort an unfinished bind as well as closing an already-listening server.
            binding.abort();
            server.close(() => resume(Effect.void));
            for (const socket of sockets.keys()) socket.destroy();
          });
          yield* Effect.forEach(pending, (closed) => Deferred.await(closed), { discard: true });
        }).pipe(
          Effect.ensuring(
            Effect.sync(() => {
              server.off('connection', receive);
              server.off('error', onError);
              server.off('listening', onListening);
            }),
          ),
        );
        return { listen, result: Deferred.await(result), close };
      },
      catch: () => failure('listen_failed', 'Could not create the desktop callback server.', true),
    }),
    (owner) => owner.close,
  );
}

export const discoverDesktopEndpointEffect = Effect.fn('okrapdf/cli.discoverDesktopEndpoint')(
  function* (options: DesktopEndpointDiscoveryOptions = {}) {
    yield* Effect.yieldNow;
    // Match Node timer normalization at the existing optional timeout boundary.
    const requestedTimeout = options.timeoutMs ?? 15000;
    const timeoutMs =
      Number.isFinite(requestedTimeout) && requestedTimeout >= 1 && requestedTimeout <= 2147483647
        ? Math.trunc(requestedTimeout)
        : 1;
    return yield* Effect.scoped(
      Effect.gen(function* () {
        const nonce = yield* Effect.try({
          try: () => randomBytes(32).toString('hex'),
          catch: () => failure('callback_invalid', 'Desktop endpoint discovery failed.', true),
        });
        const owner = yield* callbackServerEffect(nonce);
        const port = yield* owner.listen;
        const callback = new URL('okra://client-connect');
        callback.searchParams.set('port', String(port));
        callback.searchParams.set('nonce', nonce);
        const launch = options.launch
          ? Effect.tryPromise({
              try: (signal) => options.launch!(callback.toString(), signal),
              catch: (cause) =>
                cause instanceof DesktopEndpointError
                  ? privateFailure(cause)
                  : failure('launch_failed', 'LaunchServices could not open Okra.app.', true),
            })
          : defaultLaunchEffect(callback.toString());
        // A successful launcher still waits for the authenticated callback. Callback
        // completion interrupts and joins the owned launcher; foreign adapters receive a signal.
        return yield* Effect.raceFirst(owner.result, launch.pipe(Effect.andThen(Effect.never)));
      }),
    ).pipe(
      Effect.timeoutOrElse({
        duration: timeoutMs,
        orElse: () =>
          Effect.fail(
            failure('callback_timeout', 'Okra.app did not answer the local callback.', true),
          ),
      }),
    );
  },
);

export function discoverDesktopEndpoint(
  options: DesktopEndpointDiscoveryOptions = {},
  signal?: AbortSignal,
): Promise<WorkbenchEndpoint> {
  return Effect.runPromise(
    discoverDesktopEndpointEffect(options).pipe(Effect.mapError(desktopDiscoveryErrorCause)),
    { signal },
  );
}
